import React, { forwardRef, useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle } from "react";
import { Card, Tabs, Spin } from "antd";
import { getAllNotes, getCategories } from "@/services/notes";
import CreateNote from "@/components/Notes/components/CreateNote";
import { DisplayNotesType, NotesListPaginationType } from "@/components/Notes/constants";
import CustomNotesList from "./components/CustomNotesList";
import NotesList from "./components/NotesList";

const Notes = forwardRef(
  (
    {
      type,
      title,
      entityId,
      display = DisplayNotesType.Card,
      paginationType = NotesListPaginationType.Scroll,
      onNotesCountChange,
    },
    ref,
  ) => {
    const fetchedOnce = useRef(false);

    const [loadingNotes, setLoadingNotes] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null); // null means 'All'
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [notes, setNotes] = useState([]);

    const fetchCategories = useCallback(async () => {
      setLoadingCategories(true);
      getCategories(entityId)
        .then(data => {
          setCategories(data || []);
        })
        .finally(() => setLoadingCategories(false));
    }, [entityId]);

    useEffect(() => {
      fetchCategories();
    }, [fetchCategories]);

    const getNotes = useCallback(
      async (_page, _pageSize, _category) => {
        setLoadingNotes(true);

        const localPage = typeof _page === "number" ? _page : page;
        const localPageSize = typeof _pageSize === "number" ? _pageSize : pageSize;
        const localCategoryId = typeof _category !== "undefined" ? _category : selectedCategory;

        setPage(localPage);
        setPageSize(localPageSize);
        setSelectedCategory(localCategoryId);

        getAllNotes({
          id: entityId,
          page: localPage,
          pageSize: localPageSize,
          category: localCategoryId || undefined, // undefined for 'All'
        })
          .then(data => {
            setNotes(data?.items || []);
            setTotalItems(data?.totalCount || 0);
          })
          .finally(() => {
            setLoadingNotes(false);
          });
      },
      [entityId, page, pageSize, selectedCategory],
    );

    useImperativeHandle(
      ref,
      () => ({
        refresh() {
          const newPage = 1;
          const newPageSize = 10;
          fetchCategories(); // refresh categories
          getNotes(newPage, newPageSize, selectedCategory);
        },
      }),
      [getNotes, selectedCategory, fetchCategories],
    );

    useEffect(() => {
      if (fetchedOnce.current) {
        return;
      }
      fetchedOnce.current = true;
      getNotes();
    }, [getNotes]);

    const handleTabChange = key => {
      const categoryId = key === "all" ? null : key;
      setSelectedCategory(categoryId);
      getNotes(1, pageSize, categoryId);
    };

    const handlePageChange = useCallback(
      newPage => {
        getNotes(newPage, pageSize, selectedCategory);
      },
      [getNotes, pageSize, selectedCategory],
    );

    const handlePageSizeChange = useCallback(
      newPageSize => {
        getNotes(1, newPageSize, selectedCategory);
      },
      [getNotes, selectedCategory],
    );

    const notesList = useMemo(() => {
      // Use NotesList for footer pagination (Normal/Simple), CustomNotesList for infinite scroll
      if ([NotesListPaginationType.Normal, NotesListPaginationType.Simple].includes(paginationType)) {
        return (
          <NotesList
            notes={notes}
            totalItems={totalItems}
            paginationType={paginationType}
            getNotes={getNotes}
            loading={loadingNotes}
            page={page}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            onNotesCountChange={onNotesCountChange}
          />
        );
      }

      // Default to infinite scroll (CustomNotesList)
      return (
        <CustomNotesList
          notes={notes}
          totalItems={totalItems}
          getNotes={getNotes}
          loading={loadingNotes}
          page={page}
          pageSize={pageSize}
          onNotesCountChange={onNotesCountChange}
          onlyList={display === DisplayNotesType.OnlyList}
        />
      );
    }, [
      notes,
      page,
      pageSize,
      totalItems,
      loadingNotes,
      getNotes,
      onNotesCountChange,
      display,
      paginationType,
      handlePageChange,
      handlePageSizeChange,
    ]);

    const items = useMemo(() => {
      const baseItems = [
        {
          label: "All",
          key: "all",
          children: notesList,
        },
      ];

      const categoryItems = categories.map(cat => ({
        label: cat,
        key: String(cat),
        children: notesList,
      }));

      return [...baseItems, ...categoryItems];
    }, [categories, notesList]);

    if (display === DisplayNotesType.OnlyList) {
      return <div>{notesList}</div>;
    }

    return (
      <Card
        bordered={false}
        title={title || "Notes"}
        headStyle={{ borderBottom: "unset" }}
        extra={[
          <CreateNote
            key="add-note"
            type={type}
            entityId={entityId}
            getNotes={getNotes}
            onNotesCountChange={onNotesCountChange}
          />,
        ]}
      >
        {loadingCategories ? (
          <Spin />
        ) : (
          <Tabs
            activeKey={selectedCategory === null ? "all" : String(selectedCategory)}
            onChange={handleTabChange}
            type="line"
            items={items}
          />
        )}
      </Card>
    );
  },
);

Notes.displayName = "Notes";

export default Notes;
